import jStat from 'jstat';

export type DistributionType = 'normal' | 't' | 'chi-square' | 'f';

export interface CriticalValueResult {
  criticalValue: number;
  alpha: number;
  degreesOfFreedom?: number;
  distributionType: DistributionType;
  interpretation: string;
}

export interface CriticalValueParams {
  alpha: number;
  distributionType: DistributionType;
  degreesOfFreedom?: number;
  twoTailed?: boolean;
}

export function calculateCriticalValue({
  alpha,
  distributionType,
  degreesOfFreedom,
  twoTailed = true
}: CriticalValueParams): CriticalValueResult {
  let criticalValue: number;
  const effectiveAlpha = twoTailed ? alpha / 2 : alpha;

  switch (distributionType) {
    case 'normal':
      criticalValue = Math.abs(jStat.normal.inv(1 - effectiveAlpha, 0, 1));
      break;
    case 't':
      if (!degreesOfFreedom) throw new Error('Degrees of freedom required for t-distribution');
      criticalValue = Math.abs(jStat.studentt.inv(1 - effectiveAlpha, degreesOfFreedom));
      break;
    case 'chi-square':
      if (!degreesOfFreedom) throw new Error('Degrees of freedom required for chi-square distribution');
      criticalValue = jStat.chisquare.inv(1 - alpha, degreesOfFreedom);
      break;
    case 'f':
      if (!degreesOfFreedom) throw new Error('Degrees of freedom required for F-distribution');
      criticalValue = jStat.centralF.inv(1 - alpha, degreesOfFreedom, degreesOfFreedom);
      break;
    default:
      throw new Error('Invalid distribution type');
  }

  const interpretation = getInterpretation(criticalValue, distributionType, alpha, twoTailed);

  return {
    criticalValue,
    alpha,
    degreesOfFreedom,
    distributionType,
    interpretation
  };
}

function getInterpretation(
  criticalValue: number,
  distributionType: DistributionType,
  alpha: number,
  twoTailed: boolean
): string {
  const tailText = twoTailed ? 'two-tailed' : 'one-tailed';
  const alphaPercent = alpha * 100;

  switch (distributionType) {
    case 'normal':
      return `For a ${tailText} test with α = ${alphaPercent}%, reject the null hypothesis if the test statistic is ${twoTailed ? 'less than -' + criticalValue.toFixed(4) + ' or greater than ' + criticalValue.toFixed(4) : 'greater than ' + criticalValue.toFixed(4)}.`;
    case 't':
      return `For a ${tailText} t-test with α = ${alphaPercent}%, reject the null hypothesis if the t-statistic is ${twoTailed ? 'less than -' + criticalValue.toFixed(4) + ' or greater than ' + criticalValue.toFixed(4) : 'greater than ' + criticalValue.toFixed(4)}.`;
    case 'chi-square':
      return `For a chi-square test with α = ${alphaPercent}%, reject the null hypothesis if the chi-square statistic is greater than ${criticalValue.toFixed(4)}.`;
    case 'f':
      return `For an F-test with α = ${alphaPercent}%, reject the null hypothesis if the F-statistic is greater than ${criticalValue.toFixed(4)}.`;
    default:
      return '';
  }
} 